import { apiClient } from "@/services/apiClient";

/** Mirrors the backend ExpenseEntity shape. */
export type ExpenseStatus =
  | "manual"
  | "processing"
  | "ready"
  | "manual_review";

export interface Expense {
  id: string;
  companyId: string;
  createdByUserId: string | null;
  status: ExpenseStatus;
  occurredAt: string;
  vendor: string | null;
  vendorRut: string | null;
  currency: string;
  subtotal: string | null;
  iva: string | null;
  total: string;
  category: string | null;
  notes: string | null;
  imageUrl: string | null;
  ocrConfidence: string | null;
  ocrModel: string | null;
  linkedRecibidaId: string | null;
  extracted: Record<string, unknown> | null;
  supplierId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExpenseInput {
  occurredAt: string;
  vendor?: string;
  vendorRut?: string;
  currency: "UYU" | "USD";
  subtotal?: number;
  iva?: number;
  total: number;
  category?: string;
  notes?: string;
}

export type UpdateExpenseInput = Partial<CreateExpenseInput>;

export interface ListExpensesParams {
  page?: number;
  limit?: number;
  dateFrom?: string;
  dateTo?: string;
  currency?: "UYU" | "USD";
  search?: string;
  supplierId?: string;
}

export interface ListExpensesResponse {
  data: Expense[];
  total: number;
  page: number;
  limit: number;
}

export function listExpenses(
  params: ListExpensesParams = {}
): Promise<ListExpensesResponse> {
  const { page = 1, limit = 25, ...rest } = params;
  return apiClient
    .get<ListExpensesResponse>("/expenses", {
      params: {
        page,
        limit,
        ...(rest.dateFrom ? { dateFrom: rest.dateFrom } : {}),
        ...(rest.dateTo ? { dateTo: rest.dateTo } : {}),
        ...(rest.currency ? { currency: rest.currency } : {}),
        ...(rest.search?.trim() ? { search: rest.search.trim() } : {}),
        ...(rest.supplierId ? { supplierId: rest.supplierId } : {}),
      },
    })
    .then((res) => res.data);
}

export function createExpense(input: CreateExpenseInput): Promise<Expense> {
  // Strip explicit undefined / "" so optional DTO validators don't trip.
  const body: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined && value !== "") body[key] = value;
  }
  return apiClient.post<Expense>("/expenses", body).then((res) => res.data);
}

export function getExpense(id: string): Promise<Expense> {
  return apiClient.get<Expense>(`/expenses/${id}`).then((res) => res.data);
}

export function updateExpense(
  id: string,
  patch: UpdateExpenseInput
): Promise<Expense> {
  return apiClient
    .patch<Expense>(`/expenses/${id}`, patch)
    .then((res) => res.data);
}

export function deleteExpense(id: string): Promise<{ deleted: true }> {
  return apiClient
    .delete<{ deleted: true }>(`/expenses/${id}`)
    .then((res) => res.data);
}
